import React, { useState } from 'react';
import Box from '../../components/Common/Box';
import { ConfirmModal } from '../../components/Common/ConfirmModal';
import { useToast } from '../../components/Common/Toast';
import Header from '../../components/Header/Header';
import { useAuth } from '../../context/AuthContext';
import {
    DetailImport,
    LigneImport,
    OperationImport,
    StatsDelai,
    ValeurJournal,
    useFundamentalsImportLog,
    useRodageImportAuto,
} from '../../hooks/useFundamentalsImportLog';
import SiteLayout from '../../layouts/SiteLayout';

/**
 * Journal des imports de données financières (recette du 2026-10-14, lot B0, décision D8).
 *
 * Répond à « qui a écrit cette ligne, avec quel document, et qu'est-ce qui a changé ? ».
 * Un CONFLIT est une valeur d'un comparatif qui contredit la base : elle n'a pas été écrite,
 * c'est à un humain de trancher en relisant le rapport source.
 */

const LIBELLE_SOURCE: Record<string, string> = {
    PIPELINE: 'Rapport découvert',
    UPLOAD: 'Fichier téléversé',
    URL: 'Import par URL',
    SAISIE_ADMIN: 'Saisie admin',
    SCRIPT: 'Script',
    SIMULATION: 'Simulation (double circuit)',
    AUTO: 'Import automatique',
};

const LIBELLE_STATUT: Record<string, string> = {
    ECRIT: 'Écrit',
    COMPLEMENT: 'Complément seul',
    INCHANGE: 'Inchangé',
    QUARANTAINE: 'Quarantaine',
    REFUSE: 'Refusé',
    ERREUR: 'Erreur',
};

const LIBELLE_ACTION: Record<string, string> = {
    CREE: 'Créée',
    MAJ: 'Mise à jour',
    COMPLETE: 'Complétée',
    INCHANGE: 'Inchangée',
    IGNORE: 'Ignorée',
    QUARANTAINE: 'Non écrite (quarantaine)',
    SUPPRIME: 'Supprimée',
    REMPLACE: 'Remplacée',
};

const LIBELLE_CHAMP: Record<string, string> = {
    revenue: 'Chiffre d’affaires / PNB',
    ebitda: 'EBE',
    ebit: 'Résultat d’exploitation',
    ebit_reported: 'Résultat d’exploitation publié',
    net_income: 'Résultat net',
    net_income_reported: 'Résultat net publié',
    equity: 'Capitaux propres',
    financial_debt: 'Dette financière',
    cash: 'Trésorerie',
    net_debt: 'Dette nette',
    customer_loans: 'Crédits clientèle',
    customer_deposits: 'Dépôts clientèle',
    cost_of_risk: 'Coût du risque',
    closing_date: 'Date de clôture',
    commentaires: 'Note technique',
};

const COULEUR_STATUT: Record<string, { fond: string; texte: string }> = {
    ECRIT: { fond: '#def7ec', texte: '#03543f' },
    COMPLEMENT: { fond: '#e1effe', texte: '#1e429f' },
    INCHANGE: { fond: '#f3f4f6', texte: '#374151' },
    QUARANTAINE: { fond: '#ffedd5', texte: '#9a3412' },
    REFUSE: { fond: '#fdf6b2', texte: '#723b13' },
    ERREUR: { fond: '#fde8e8', texte: '#9b1c1c' },
};

const th: React.CSSProperties = { color: 'white', padding: '12px 14px', textAlign: 'left', fontSize: '12px', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { padding: '10px 14px', textAlign: 'left', fontSize: '13px', verticalAlign: 'top' };
const tdDroite: React.CSSProperties = { ...td, textAlign: 'right' };

const badge = (statut: string): React.CSSProperties => {
    const c = COULEUR_STATUT[statut] ?? COULEUR_STATUT.INCHANGE;
    return { padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, backgroundColor: c.fond, color: c.texte };
};

const fmtDate = (iso: string | null): string =>
    iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} ${iso.slice(11, 16)}` : '—';

const fmtValeur = (v: ValeurJournal): string => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') return `${v.toLocaleString('fr-FR')} F`;
    return String(v);
};

// Une simulation n'écrit rien : son statut dit ce que l'import réel AURAIT fait.
const libelleStatut = (op: OperationImport): string => {
    const l = LIBELLE_STATUT[op.statut] ?? op.statut;
    return op.source === 'SIMULATION' ? `${l} (simulé)` : l;
};

const fmtDelai = (s: StatsDelai): string =>
    s.nb === 0 ? '—' : `médiane ${s.mediane?.toLocaleString('fr-FR')} j, max ${s.max} j (${s.nb} rapport${s.nb > 1 ? 's' : ''})`;

const fmtJour = (iso: string | null): string => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');

const Indicateur: React.FC<{ libelle: string; valeur: string; detail?: string }> = ({ libelle, valeur, detail }) => (
    <div style={{ border: '1px solid var(--border-color, #e5e7eb)', borderRadius: '8px', padding: '10px 14px', minWidth: '180px', flex: '1 1 180px' }}>
        <p style={{ margin: 0, fontSize: '12px', opacity: 0.75 }}>{libelle}</p>
        <p style={{ margin: '4px 0 0', fontSize: '20px', fontWeight: 700 }}>{valeur}</p>
        {detail && <p style={{ margin: '2px 0 0', fontSize: '12px', opacity: 0.75 }}>{detail}</p>}
    </div>
);

/**
 * Rapport de rodage du lot B10 (décision D6) : la bascule de l'import automatique se décide
 * après 4 semaines de double circuit sans écart non expliqué entre simulation et import validé.
 */
const PanneauRodage: React.FC<{ ouvrir: (id: number) => void }> = ({ ouvrir }) => {
    const { rapport, loading, error, basculer } = useRodageImportAuto();
    const { showToast } = useToast();
    // État demandé en attente de confirmation (null = aucune demande en cours).
    const [demande, setDemande] = useState<boolean | null>(null);
    const [enCours, setEnCours] = useState(false);

    const confirmer = async () => {
        if (demande === null) return;
        setEnCours(true);
        const ok = await basculer(demande);
        setEnCours(false);
        setDemande(null);
        if (ok) {
            showToast(
                demande
                    ? 'Import automatique activé : les rapports éligibles seront écrits sans validation humaine.'
                    : 'Import automatique désactivé : seule la simulation continue.',
                demande ? 'warning' : 'success'
            );
        } else {
            showToast('La bascule n’a pas été enregistrée. L’état affiché reste celui de la plateforme.', 'error');
        }
    };

    if (loading) {
        return (
            <Box>
                <p style={{ padding: '14px 20px', margin: 0 }}>Chargement du rodage de l’import automatique…</p>
            </Box>
        );
    }
    if (error || !rapport) {
        return (
            <Box>
                <p style={{ padding: '14px 20px', color: '#9b1c1c', margin: 0 }}>{error ?? 'Rodage indisponible.'}</p>
            </Box>
        );
    }
    const c = rapport.comparaison;
    const motifs = Object.entries(rapport.motifs_file_humaine);
    return (
        <Box>
            <div className='box-title box-vertical-padding box-horizontal-padding'>
                <h3 style={{ margin: 0 }}>Rodage de l’import automatique — 4 dernières semaines</h3>
            </div>
            <div className='box-horizontal-padding box-vertical-padding'>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', margin: '0 0 12px' }}>
                    <button
                        type='button'
                        role='switch'
                        aria-checked={rapport.interrupteur.actif}
                        aria-label='Import automatique des rapports financiers'
                        onClick={() => setDemande(!rapport.interrupteur.actif)}
                        disabled={enCours}
                        style={{
                            width: '46px',
                            height: '26px',
                            borderRadius: '13px',
                            border: 'none',
                            cursor: enCours ? 'wait' : 'pointer',
                            position: 'relative',
                            backgroundColor: rapport.interrupteur.actif ? '#16a34a' : '#9ca3af',
                            transition: 'background-color 0.2s',
                            flexShrink: 0,
                        }}
                    >
                        <span
                            style={{
                                position: 'absolute',
                                top: '3px',
                                left: rapport.interrupteur.actif ? '23px' : '3px',
                                width: '20px',
                                height: '20px',
                                borderRadius: '50%',
                                backgroundColor: 'white',
                                transition: 'left 0.2s',
                            }}
                        />
                    </button>
                    <span style={{ fontSize: '14px' }}>
                        Import automatique :{' '}
                        <strong style={{ color: rapport.interrupteur.actif ? '#03543f' : '#9a3412' }}>
                            {rapport.interrupteur.actif ? 'ACTIVÉ' : 'désactivé (simulation seule)'}
                        </strong>
                        {rapport.interrupteur.modifie_le && (
                            <span style={{ fontSize: '12px', opacity: 0.75 }}>
                                {' '}
                                · modifié le {fmtDate(rapport.interrupteur.modifie_le)}
                            </span>
                        )}
                    </span>
                    <span style={{ fontSize: '13px', opacity: 0.85 }}>
                        Première simulation : {fmtJour(rapport.premiere_simulation)} · fin du rodage au plus tôt : {fmtJour(rapport.fin_rodage_au_plus_tot)}
                    </span>
                </div>
                <ConfirmModal
                    isOpen={demande !== null}
                    title={demande ? 'Activer l’import automatique ?' : 'Désactiver l’import automatique ?'}
                    message={
                        demande
                            ? 'Les rapports financiers découverts sur brvm.org qui remplissent tous les critères (période certaine, aucun invariant violé, aucun conflit, ' +
                              'aucune valeur existante réécrite de plus de 1 %, seul document de sa période) seront écrits dans les données financières SANS validation ' +
                              'humaine, et leur article proposé sera généré. Les autres restent dans votre file.' +
                              (rapport.fin_rodage_au_plus_tot === null
                                  ? ' Aucune simulation n’a encore eu lieu : le rodage de 4 semaines n’a pas commencé.'
                                  : rapport.fin_rodage_au_plus_tot > new Date().toISOString().slice(0, 10)
                                    ? ` Le rodage de 4 semaines n’est pas terminé (fin au plus tôt le ${fmtJour(rapport.fin_rodage_au_plus_tot)}).`
                                    : '')
                            : 'Les nouveaux rapports ne seront plus écrits automatiquement : la simulation continue, et l’import redevient manuel depuis l’écran des rapports découverts.'
                    }
                    confirmLabel={demande ? 'Activer' : 'Désactiver'}
                    cancelLabel='Annuler'
                    isDestructive={demande === true}
                    isLoading={enCours}
                    onConfirm={confirmer}
                    onCancel={() => setDemande(null)}
                />
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <Indicateur
                        libelle='Simulations identiques à l’import validé'
                        valeur={c.taux_identiques === null ? '—' : `${(c.taux_identiques * 100).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`}
                        detail={`${c.nb_identiques} sur ${c.nb_comparables} comparable${c.nb_comparables > 1 ? 's' : ''}`}
                    />
                    <Indicateur
                        libelle='Rapports simulés'
                        valeur={String(rapport.nb_rapports)}
                        detail={`${rapport.nb_eligibles} éligible${rapport.nb_eligibles > 1 ? 's' : ''} · ${c.nb_en_attente_d_import_admin} en attente d’import admin`}
                    />
                    <Indicateur
                        libelle='Quarantaines simulées'
                        valeur={String(rapport.quarantaines.nb)}
                        detail={`${rapport.quarantaines.forcees_par_admin} forcée(s) par un admin · ${rapport.quarantaines.en_attente} en attente`}
                    />
                    <Indicateur libelle='Délai publication → import (admin)' valeur={fmtDelai(rapport.delai_publication_import_jours.admin)} />
                    <Indicateur libelle='Délai publication → import (auto)' valeur={fmtDelai(rapport.delai_publication_import_jours.automatique)} />
                </div>
                {motifs.length > 0 && (
                    <>
                        <p style={{ margin: '14px 0 4px', fontWeight: 600, fontSize: '13px' }}>Motifs de file humaine</p>
                        <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px' }}>
                            {motifs.map(([m, n]) => (
                                <li key={m}>
                                    {rapport.libelles_motifs[m] ?? m} : {n}
                                </li>
                            ))}
                        </ul>
                    </>
                )}
                {c.ecarts.length > 0 && (
                    <>
                        <p style={{ margin: '14px 0 4px', fontWeight: 600, fontSize: '13px' }}>
                            Écarts entre simulation et import validé (à expliquer avant la bascule)
                        </p>
                        <table className='data-table' style={{ width: '100%' }}>
                            <thead>
                                <tr style={{ background: 'var(--bg-hover)' }}>
                                    <th style={{ ...td, fontWeight: 600 }}>Société</th>
                                    <th style={{ ...td, fontWeight: 600 }}>Statuts simulé → validé</th>
                                    <th style={{ ...td, fontWeight: 600 }}>Champs différents</th>
                                    <th style={{ ...td, fontWeight: 600 }}>Opérations</th>
                                </tr>
                            </thead>
                            <tbody>
                                {c.ecarts.map((e) => (
                                    <tr key={e.simulation_id}>
                                        <td style={td}>
                                            <strong>{e.societe_symbole ?? '—'}</strong>
                                        </td>
                                        <td style={td}>
                                            {LIBELLE_STATUT[e.statut_simulation] ?? e.statut_simulation} → {LIBELLE_STATUT[e.statut_valide] ?? e.statut_valide}
                                            {e.force_par_admin && ' (forcé par un admin)'}
                                        </td>
                                        <td style={td}>
                                            {e.ecarts.length === 0
                                                ? '—'
                                                : e.ecarts.map((x, i) => (
                                                      <div key={i}>
                                                          {x.periode} · {LIBELLE_CHAMP[x.champ] ?? x.champ} : {fmtValeur(x.simulation)} → {fmtValeur(x.valide)}
                                                      </div>
                                                  ))}
                                        </td>
                                        <td style={td}>
                                            <button className='button' onClick={() => ouvrir(e.simulation_id)}>
                                                Simulation
                                            </button>{' '}
                                            <button className='button' onClick={() => ouvrir(e.import_id)}>
                                                Import
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>
        </Box>
    );
};

const periodeDocument = (op: OperationImport): string =>
    op.document_period_type && op.document_exercice
        ? `${op.document_period_type} ${op.document_exercice}`
        : 'indéterminée';

const TableauChamps: React.FC<{
    titre: string;
    valeurs: Record<string, [ValeurJournal, ValeurJournal]>;
    libelleAvant: string;
    libelleApres: string;
}> = ({ titre, valeurs, libelleAvant, libelleApres }) => {
    const champs = Object.keys(valeurs);
    if (champs.length === 0) return null;
    return (
        <div style={{ margin: '8px 0 12px' }}>
            <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: '13px' }}>{titre}</p>
            <table className='data-table' style={{ width: '100%' }}>
                <thead>
                    <tr style={{ background: 'var(--bg-hover)' }}>
                        <th style={{ ...td, fontWeight: 600 }}>Champ</th>
                        <th style={{ ...tdDroite, fontWeight: 600 }}>{libelleAvant}</th>
                        <th style={{ ...tdDroite, fontWeight: 600 }}>{libelleApres}</th>
                    </tr>
                </thead>
                <tbody>
                    {champs.map((c) => (
                        <tr key={c}>
                            <td style={td}>{LIBELLE_CHAMP[c] ?? c}</td>
                            <td style={tdDroite}>{fmtValeur(valeurs[c][0])}</td>
                            <td style={tdDroite}>{fmtValeur(valeurs[c][1])}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const BlocLigne: React.FC<{ ligne: LigneImport }> = ({ ligne }) => (
    <div style={{ border: '1px solid var(--border-color, #e5e7eb)', borderRadius: '8px', padding: '12px 14px', marginBottom: '12px' }}>
        <p style={{ margin: '0 0 6px' }}>
            <strong>
                {ligne.period_type} {ligne.exercice}
            </strong>{' '}
            · {ligne.role === 'PRINCIPAL' ? 'période du document' : ligne.role === 'COMPLEMENT' ? 'comparatif ou période annexe' : 'saisie manuelle'}{' '}
            · <span style={badge(ligne.action === 'QUARANTAINE' ? 'QUARANTAINE' : ligne.action === 'INCHANGE' || ligne.action === 'IGNORE' ? 'INCHANGE' : 'ECRIT')}>
                {LIBELLE_ACTION[ligne.action] ?? ligne.action}
            </span>
        </p>
        <TableauChamps titre={ligne.action === 'QUARANTAINE' ? 'Valeurs NON écrites (quarantaine)' : 'Valeurs écrites'} valeurs={ligne.ecritures} libelleAvant='Avant' libelleApres='Après' />
        <TableauChamps
            titre='Conflits non écrits (à relire sur le rapport source)'
            valeurs={ligne.conflits}
            libelleAvant='En base'
            libelleApres='Extrait'
        />
        {ligne.alertes.length > 0 && (
            <ul style={{ margin: '4px 0 0', paddingLeft: '18px', fontSize: '13px' }}>
                {ligne.alertes.map((a, i) => (
                    <li key={i}>{a}</li>
                ))}
            </ul>
        )}
    </div>
);

const PanneauDetail: React.FC<{ detail: DetailImport; fermer: () => void }> = ({ detail, fermer }) => (
    <Box>
        <div className='box-title box-vertical-padding box-horizontal-padding' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>
                Opération n° {detail.id} — {detail.societe_symbole ?? '—'} · {LIBELLE_SOURCE[detail.source] ?? detail.source}
            </h3>
            <button className='button' onClick={fermer}>
                Fermer
            </button>
        </div>
        <div className='box-horizontal-padding box-vertical-padding'>
            <p style={{ margin: '0 0 4px' }}>
                {fmtDate(detail.created_at)} · période du document : <strong>{periodeDocument(detail)}</strong> ·{' '}
                <span style={badge(detail.statut)}>{libelleStatut(detail)}</span>
            </p>
            {detail.fichier && <p style={{ margin: '0 0 4px', fontSize: '13px' }}>Fichier : {detail.fichier}</p>}
            {detail.source_url && (
                <p style={{ margin: '0 0 4px', fontSize: '13px' }}>
                    Source :{' '}
                    <a href={detail.source_url} target='_blank' rel='noopener noreferrer'>
                        rapport sur brvm.org
                    </a>
                </p>
            )}
            {detail.message && <p style={{ margin: '0 0 8px', fontSize: '13px' }}>Message : {detail.message}</p>}
            <div style={{ marginTop: '12px' }}>
                {detail.lignes.length === 0 ? (
                    <p>Aucune ligne financière touchée.</p>
                ) : (
                    detail.lignes.map((l, i) => <BlocLigne key={i} ligne={l} />)
                )}
            </div>
            {detail.alertes.length > 0 && (
                <>
                    <p style={{ margin: '8px 0 4px', fontWeight: 600 }}>Alertes de l’opération</p>
                    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13px' }}>
                        {detail.alertes.map((a, i) => (
                            <li key={i}>{a}</li>
                        ))}
                    </ul>
                </>
            )}
            {detail.items_bruts !== null && detail.items_bruts !== undefined && (
                <details style={{ marginTop: '12px' }}>
                    <summary style={{ cursor: 'pointer' }}>Réponse brute de l’extraction (avant contrôle)</summary>
                    <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', maxHeight: '320px', overflow: 'auto' }}>
                        {JSON.stringify(detail.items_bruts, null, 2)}
                    </pre>
                </details>
            )}
        </div>
    </Box>
);

const AdminImportJournalScreen: React.FC = () => {
    const { hasPermission } = useAuth();
    const j = useFundamentalsImportLog();

    if (!hasPermission('admin_platform')) {
        return (
            <SiteLayout>
                <Box>
                    <p style={{ padding: '20px' }}>Accès refusé. Permission admin_platform requise.</p>
                </Box>
            </SiteLayout>
        );
    }

    const pages = Math.max(1, Math.ceil(j.total / j.taillePage));

    return (
        <SiteLayout>
            <Header icon='history' title='Journal des imports financiers' />

            <PanneauRodage ouvrir={j.ouvrir} />

            <Box>
                <div className='box-horizontal-padding box-vertical-padding' style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <input
                        type='text'
                        placeholder='Symbole (ex. BOABF)'
                        value={j.filtres.symbole}
                        onChange={(e) => j.changerFiltres({ symbole: e.target.value })}
                        style={{ padding: '8px 10px', width: '160px' }}
                    />
                    <select value={j.filtres.source} onChange={(e) => j.changerFiltres({ source: e.target.value })} style={{ padding: '8px 10px' }}>
                        <option value=''>Toutes les sources</option>
                        {Object.entries(LIBELLE_SOURCE).map(([k, v]) => (
                            <option key={k} value={k}>
                                {v}
                            </option>
                        ))}
                    </select>
                    <select value={j.filtres.statut} onChange={(e) => j.changerFiltres({ statut: e.target.value })} style={{ padding: '8px 10px' }}>
                        <option value=''>Tous les statuts</option>
                        {Object.entries(LIBELLE_STATUT).map(([k, v]) => (
                            <option key={k} value={k}>
                                {v}
                            </option>
                        ))}
                    </select>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}>
                        <input type='checkbox' checked={j.filtres.aRevoir} onChange={(e) => j.changerFiltres({ aRevoir: e.target.checked })} />
                        À revoir (conflits ou alertes)
                    </label>
                    <span style={{ marginLeft: 'auto', fontSize: '13px' }}>
                        {j.total.toLocaleString('fr-FR')} opération{j.total > 1 ? 's' : ''}
                    </span>
                </div>
            </Box>

            {j.error && (
                <Box>
                    <p style={{ padding: '14px 20px', color: '#9b1c1c', margin: 0 }}>{j.error}</p>
                </Box>
            )}

            {j.detail && <PanneauDetail detail={j.detail} fermer={j.fermer} />}

            <Box>
                <div style={{ overflowX: 'auto' }}>
                    <table className='data-table' style={{ width: '100%' }}>
                        <thead>
                            <tr style={{ background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)' }}>
                                <th style={th}>Date</th>
                                <th style={th}>Société</th>
                                <th style={th}>Source</th>
                                <th style={th}>Période du document</th>
                                <th style={th}>Statut</th>
                                <th style={{ ...th, textAlign: 'right' }}>Lignes écrites</th>
                                <th style={{ ...th, textAlign: 'right' }}>Conflits</th>
                                <th style={{ ...th, textAlign: 'right' }}>Alertes</th>
                                <th style={th}>Fichier</th>
                            </tr>
                        </thead>
                        <tbody>
                            {j.loading && (
                                <tr>
                                    <td style={td} colSpan={9}>
                                        Chargement…
                                    </td>
                                </tr>
                            )}
                            {!j.loading && j.operations.length === 0 && (
                                <tr>
                                    <td style={td} colSpan={9}>
                                        Aucune opération ne correspond à ces filtres.
                                    </td>
                                </tr>
                            )}
                            {!j.loading &&
                                j.operations.map((op, i) => (
                                    <tr
                                        key={op.id}
                                        onClick={() => j.ouvrir(op.id)}
                                        style={{ cursor: 'pointer', backgroundColor: i % 2 === 0 ? 'transparent' : 'var(--bg-hover)' }}
                                        title='Voir le détail champ par champ'
                                    >
                                        <td style={td}>{fmtDate(op.created_at)}</td>
                                        <td style={td}>
                                            <strong>{op.societe_symbole ?? '—'}</strong>
                                        </td>
                                        <td style={td}>{LIBELLE_SOURCE[op.source] ?? op.source}</td>
                                        <td style={td}>{periodeDocument(op)}</td>
                                        <td style={td}>
                                            <span style={badge(op.statut)}>{libelleStatut(op)}</span>
                                        </td>
                                        <td style={tdDroite}>{op.nb_lignes_ecrites}</td>
                                        <td style={{ ...tdDroite, color: op.nb_conflits > 0 ? '#9b1c1c' : undefined, fontWeight: op.nb_conflits > 0 ? 600 : undefined }}>
                                            {op.nb_conflits}
                                        </td>
                                        <td style={tdDroite}>{op.nb_alertes}</td>
                                        <td style={{ ...td, maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {op.fichier ?? '—'}
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
                {pages > 1 && (
                    <div className='box-horizontal-padding box-vertical-padding' style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <button className='button' disabled={j.page === 0} onClick={() => j.setPage(j.page - 1)}>
                            Précédent
                        </button>
                        <span style={{ fontSize: '13px' }}>
                            Page {j.page + 1} / {pages}
                        </span>
                        <button className='button' disabled={j.page + 1 >= pages} onClick={() => j.setPage(j.page + 1)}>
                            Suivant
                        </button>
                    </div>
                )}
            </Box>
            {j.detailLoading && (
                <Box>
                    <p style={{ padding: '14px 20px', margin: 0 }}>Chargement du détail…</p>
                </Box>
            )}
        </SiteLayout>
    );
};

export default AdminImportJournalScreen;
